/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import { cleanCnpj, validateCnpj } from 'utils/cnpj';
import {
  countryCodeSafeMaskProps,
  isCelularValid,
  isTelefoneValid,
  phoneDigits,
} from 'utils/phone';
import {
  CNPJ_MASK,
  EMAIL_REGEX,
  MAX_LIST_ROWS,
  inputClass,
  isFullName,
  nextRowId,
} from '../helpers';
import { CompanyRequesterValues, YesNoChoice } from '../types';

type FieldName = Exclude<keyof CompanyRequesterValues, 'outros_cnpjs'>;
type TextFieldName = Exclude<FieldName, 'usa_outros_cnpjs'>;

interface CompanyRequesterErrors {
  fields: Partial<Record<FieldName, string>>;
  otherCnpjs: Record<number, string>;
}

interface CompanyRequesterStepProps {
  values: CompanyRequesterValues;
  companyLocked: boolean;
  onChange: (values: CompanyRequesterValues) => void;
  onNext: () => void;
  onCancel: () => void;
  onReturnToReview: (() => void) | undefined;
}

const validate = (values: CompanyRequesterValues): CompanyRequesterErrors => {
  const fields: CompanyRequesterErrors['fields'] = {};
  const otherCnpjs: CompanyRequesterErrors['otherCnpjs'] = {};

  if (values.razao_social.trim().length < 2) {
    fields.razao_social = 'Informe a razão social.';
  }

  if (!validateCnpj(values.cnpj_contratante)) {
    fields.cnpj_contratante = 'Informe um CNPJ válido.';
  }

  if (!values.usa_outros_cnpjs) {
    fields.usa_outros_cnpjs = 'Selecione uma opção.';
  } else if (values.usa_outros_cnpjs === 'sim') {
    if (values.outros_cnpjs.length === 0) {
      fields.usa_outros_cnpjs = 'Adicione pelo menos um CNPJ ou marque "Não".';
    }

    const contractorCnpj = cleanCnpj(values.cnpj_contratante);
    const seen: string[] = [];

    values.outros_cnpjs.forEach(({ id, cnpj }) => {
      const clean = cleanCnpj(cnpj);

      if (!clean) {
        otherCnpjs[id] = 'Informe o CNPJ ou remova a linha.';
      } else if (!validateCnpj(clean)) {
        otherCnpjs[id] = 'Informe um CNPJ válido.';
      } else if (clean === contractorCnpj) {
        otherCnpjs[id] = 'Este é o CNPJ da empresa contratante.';
      } else if (seen.includes(clean)) {
        otherCnpjs[id] = 'CNPJ repetido.';
      }

      seen.push(clean);
    });
  }

  if (!isFullName(values.nome_solicitante)) {
    fields.nome_solicitante = 'Informe seu nome completo.';
  }

  if (!EMAIL_REGEX.test(values.email_corporativo.trim())) {
    fields.email_corporativo = 'Informe um e-mail válido.';
  }

  if (!isCelularValid(values.telefone_celular)) {
    fields.telefone_celular = 'Informe o celular com DDD.';
  }

  if (phoneDigits(values.telefone_fixo) && !isTelefoneValid(values.telefone_fixo)) {
    fields.telefone_fixo = 'Informe o telefone com DDD ou deixe em branco.';
  }

  return { fields, otherCnpjs };
};

const hasErrors = (errors: CompanyRequesterErrors) =>
  Object.keys(errors.fields).length > 0 || Object.keys(errors.otherCnpjs).length > 0;

export const isCompanyRequesterComplete = (values: CompanyRequesterValues) =>
  !hasErrors(validate(values));

const CompanyRequesterStep: React.FC<CompanyRequesterStepProps> = ({
  values,
  companyLocked,
  onChange,
  onNext,
  onCancel,
  onReturnToReview,
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const errors = useMemo(() => validate(values), [values]);
  const isValid = !hasErrors(errors);

  const touch = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const fieldError = (field: FieldName) =>
    submitted || touched[field] ? errors.fields[field] : undefined;

  const otherCnpjError = (id: number) =>
    submitted || touched[`outros_cnpjs.${id}`] ? errors.otherCnpjs[id] : undefined;

  const handleChange = (field: TextFieldName, value: string) =>
    onChange({ ...values, [field]: value });

  const handleOtherCnpjsChoice = (choice: YesNoChoice) => {
    const rows = values.outros_cnpjs.length > 0 ? values.outros_cnpjs : [{ id: 1, cnpj: '' }];

    onChange({ ...values, usa_outros_cnpjs: choice, outros_cnpjs: choice === 'sim' ? rows : [] });
  };

  const addOtherCnpj = () =>
    onChange({
      ...values,
      outros_cnpjs: [...values.outros_cnpjs, { id: nextRowId(values.outros_cnpjs), cnpj: '' }],
    });

  const updateOtherCnpj = (id: number, cnpj: string) =>
    onChange({
      ...values,
      outros_cnpjs: values.outros_cnpjs.map((row) => (row.id === id ? { ...row, cnpj } : row)),
    });

  const removeOtherCnpj = (id: number) => {
    setTouched((prev) => {
      const next = { ...prev };
      delete next[`outros_cnpjs.${id}`];
      return next;
    });

    onChange({ ...values, outros_cnpjs: values.outros_cnpjs.filter((row) => row.id !== id) });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (isValid) onNext();
  };

  const handleReturnToReview = () => {
    setSubmitted(true);

    if (isValid && onReturnToReview) onReturnToReview();
  };

  const razaoSocialError = fieldError('razao_social');
  const cnpjError = fieldError('cnpj_contratante');
  const otherCnpjsChoiceError = fieldError('usa_outros_cnpjs');
  const nameError = fieldError('nome_solicitante');
  const emailError = fieldError('email_corporativo');
  const cellphoneError = fieldError('telefone_celular');
  const phoneError = fieldError('telefone_fixo');

  return (
    <form noValidate onSubmit={handleSubmit}>
      <p className="card-text font-weight-bold mt-4">Empresa contratante</p>
      <div className="row">
        <div className="col-md-7 mb-4">
          <label htmlFor="api_request_razao_social">Razão social</label>
          <input
            id="api_request_razao_social"
            type="text"
            className={inputClass(razaoSocialError)}
            value={values.razao_social}
            readOnly={companyLocked}
            onChange={(e) => handleChange('razao_social', e.target.value)}
            onBlur={() => touch('razao_social')}
          />
          {razaoSocialError && <div className="invalid-feedback">{razaoSocialError}</div>}
        </div>
        <div className="col-md-5 mb-4">
          <label htmlFor="api_request_cnpj_contratante">CNPJ</label>
          <InputMask
            id="api_request_cnpj_contratante"
            type="text"
            mask={CNPJ_MASK}
            className={inputClass(cnpjError)}
            value={values.cnpj_contratante}
            readOnly={companyLocked}
            onChange={(e) => handleChange('cnpj_contratante', e.target.value.toUpperCase())}
            onBlur={() => touch('cnpj_contratante')}
          />
          {cnpjError && <div className="invalid-feedback">{cnpjError}</div>}
        </div>
      </div>
      {companyLocked && (
        <small className="form-text text-muted mt-n3 mb-4">
          Dados da empresa em que você está logado.
        </small>
      )}

      <fieldset className="mb-4">
        <legend className="col-form-label pt-0">
          Sua empresa também usa ou já usou o ConLicitação com outros CNPJs?
        </legend>
        <div className="form-check form-check-inline">
          <input
            id="api_request_outros_cnpjs_sim"
            type="radio"
            name="api_request_usa_outros_cnpjs"
            className={`form-check-input ${otherCnpjsChoiceError ? 'is-invalid' : ''}`}
            checked={values.usa_outros_cnpjs === 'sim'}
            onChange={() => handleOtherCnpjsChoice('sim')}
          />
          <label className="form-check-label" htmlFor="api_request_outros_cnpjs_sim">
            Sim
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            id="api_request_outros_cnpjs_nao"
            type="radio"
            name="api_request_usa_outros_cnpjs"
            className={`form-check-input ${otherCnpjsChoiceError ? 'is-invalid' : ''}`}
            checked={values.usa_outros_cnpjs === 'nao'}
            onChange={() => handleOtherCnpjsChoice('nao')}
          />
          <label className="form-check-label" htmlFor="api_request_outros_cnpjs_nao">
            Não
          </label>
        </div>
        {otherCnpjsChoiceError && (
          <div className="invalid-feedback d-block">{otherCnpjsChoiceError}</div>
        )}

        {values.usa_outros_cnpjs === 'sim' && (
          <div className="mt-3">
            {values.outros_cnpjs.map((row, index) => {
              const rowError = otherCnpjError(row.id);
              const inputId = `api_request_outro_cnpj_${row.id}`;

              return (
                <div key={row.id} className="row">
                  <div className="col-10 col-md-6 mb-3">
                    <label className="sr-only" htmlFor={inputId}>
                      CNPJ {index + 1}
                    </label>
                    <InputMask
                      id={inputId}
                      type="text"
                      mask={CNPJ_MASK}
                      placeholder="CNPJ"
                      className={inputClass(rowError)}
                      value={row.cnpj}
                      onChange={(e) => updateOtherCnpj(row.id, e.target.value.toUpperCase())}
                      onBlur={() => touch(`outros_cnpjs.${row.id}`)}
                    />
                    {rowError && <div className="invalid-feedback">{rowError}</div>}
                  </div>
                  <div className="col-2 col-md-1 mb-3">
                    <button
                      type="button"
                      className="btn btn-link text-secondary px-0"
                      aria-label={`Remover CNPJ ${index + 1}`}
                      onClick={() => removeOtherCnpj(row.id)}
                    >
                      <span className="material-icons" aria-hidden="true">
                        close
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
            {values.outros_cnpjs.length < MAX_LIST_ROWS && (
              <div className="text-right">
                <button type="button" className="btn btn-link" onClick={addOtherCnpj}>
                  + adicionar CNPJ
                </button>
              </div>
            )}
          </div>
        )}
      </fieldset>

      <p className="card-text font-weight-bold mt-4">Solicitante</p>
      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_nome_solicitante">Nome completo</label>
          <input
            id="api_request_nome_solicitante"
            type="text"
            autoComplete="name"
            className={inputClass(nameError)}
            value={values.nome_solicitante}
            onChange={(e) => handleChange('nome_solicitante', e.target.value)}
            onBlur={() => touch('nome_solicitante')}
          />
          {nameError && <div className="invalid-feedback">{nameError}</div>}
        </div>
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_email_corporativo">E-mail corporativo</label>
          <input
            id="api_request_email_corporativo"
            type="email"
            autoComplete="email"
            className={inputClass(emailError)}
            value={values.email_corporativo}
            onChange={(e) => handleChange('email_corporativo', e.target.value)}
            onBlur={() => touch('email_corporativo')}
          />
          {emailError && <div className="invalid-feedback">{emailError}</div>}
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_telefone_celular">Celular</label>
          <InputMask
            {...countryCodeSafeMaskProps}
            id="api_request_telefone_celular"
            type="tel"
            mask="(99) 99999-9999"
            className={inputClass(cellphoneError)}
            value={values.telefone_celular}
            onChange={(e) => handleChange('telefone_celular', e.target.value)}
            onBlur={() => touch('telefone_celular')}
          />
          {cellphoneError && <div className="invalid-feedback">{cellphoneError}</div>}
        </div>
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_telefone_fixo">
            Telefone fixo <span className="text-muted">(opcional)</span>
          </label>
          <InputMask
            {...countryCodeSafeMaskProps}
            id="api_request_telefone_fixo"
            type="tel"
            mask="(99) 9999-9999"
            className={inputClass(phoneError)}
            value={values.telefone_fixo}
            onChange={(e) => handleChange('telefone_fixo', e.target.value)}
            onBlur={() => touch('telefone_fixo')}
          />
          {phoneError && <div className="invalid-feedback">{phoneError}</div>}
        </div>
      </div>

      {submitted && !isValid && (
        <div className="alert alert-danger text-center py-2" role="alert">
          Revise os campos destacados para continuar.
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <button type="submit" className="btn btn-lg btn-block btn-submit">
            Continuar
          </button>
          <div className="text-center mt-2">
            {onReturnToReview && (
              <button type="button" className="btn btn-link" onClick={handleReturnToReview}>
                Voltar para a revisão
              </button>
            )}
            <button type="button" className="btn btn-link" onClick={onCancel}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CompanyRequesterStep;
